import { z } from 'zod';
import { HexColor, Id, UnitInterval } from './common.js';

const EPS = 1e-9;

export const AnnotationKind = z.enum(['rect', 'ellipse', 'path', 'arrow', 'text']);
export type AnnotationKind = z.infer<typeof AnnotationKind>;

/** Max points per freehand path after RDP simplification (ε = 0.002). */
export const ANNOTATION_PATH_MAX_POINTS = 2000;
export const ANNOTATION_PATH_RDP_EPSILON = 0.002;
export const ANNOTATION_DEFAULT_STROKE_WIDTH_REL = 0.004;
export const ANNOTATION_MAX_PER_COMMENT = 50;

export const RectGeometry = z
  .object({ x: UnitInterval, y: UnitInterval, w: UnitInterval, h: UnitInterval })
  .refine((g) => g.x + g.w <= 1 + EPS && g.y + g.h <= 1 + EPS, {
    message: 'rect exceeds the 0–1 media bounds',
  });
export type RectGeometry = z.infer<typeof RectGeometry>;

export const EllipseGeometry = z
  .object({ cx: UnitInterval, cy: UnitInterval, rx: UnitInterval, ry: UnitInterval })
  .refine(
    (g) =>
      g.cx - g.rx >= -EPS &&
      g.cx + g.rx <= 1 + EPS &&
      g.cy - g.ry >= -EPS &&
      g.cy + g.ry <= 1 + EPS,
    {
      message: 'ellipse exceeds the 0–1 media bounds',
    },
  );
export type EllipseGeometry = z.infer<typeof EllipseGeometry>;

export const PathPoint = z.tuple([UnitInterval, UnitInterval]);
export type PathPoint = z.infer<typeof PathPoint>;

/** Freehand path, simplified with Ramer–Douglas–Peucker (ε = 0.002), max 2,000 points. */
export const PathGeometry = z.object({
  points: z.array(PathPoint).min(2).max(ANNOTATION_PATH_MAX_POINTS),
});
export type PathGeometry = z.infer<typeof PathGeometry>;

export const ArrowGeometry = z.object({
  x1: UnitInterval,
  y1: UnitInterval,
  x2: UnitInterval,
  y2: UnitInterval,
});
export type ArrowGeometry = z.infer<typeof ArrowGeometry>;

export const TextGeometry = z.object({
  x: UnitInterval,
  y: UnitInterval,
  text: z.string().max(500),
  /** Fraction of media height. */
  font_size_rel: z.number().gt(0).max(1),
});
export type TextGeometry = z.infer<typeof TextGeometry>;

/**
 * `oneOf` of the five geometry shapes (OpenAPI `AnnotationGeometry`). Discriminated by the
 * parent `kind` — use `AnnotationInput` for a kind-aware discriminated union.
 */
export const AnnotationGeometry = z.union([
  RectGeometry,
  EllipseGeometry,
  PathGeometry,
  ArrowGeometry,
  TextGeometry,
]);
export type AnnotationGeometry = z.infer<typeof AnnotationGeometry>;

const annotationStyle = {
  stroke_color: HexColor.optional(),
  /** Fraction of media width. */
  stroke_width_rel: z.number().gt(0).max(0.1).default(ANNOTATION_DEFAULT_STROKE_WIDTH_REL),
  label: z.string().max(120).nullable().optional(),
};

/** Discriminated on `kind`; each variant validates the matching geometry with 0–1 refinements. */
export const AnnotationInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('rect'), geometry: RectGeometry, ...annotationStyle }),
  z.object({ kind: z.literal('ellipse'), geometry: EllipseGeometry, ...annotationStyle }),
  z.object({ kind: z.literal('path'), geometry: PathGeometry, ...annotationStyle }),
  z.object({ kind: z.literal('arrow'), geometry: ArrowGeometry, ...annotationStyle }),
  z.object({ kind: z.literal('text'), geometry: TextGeometry, ...annotationStyle }),
]);
export type AnnotationInput = z.infer<typeof AnnotationInput>;

const annotationIds = { id: Id, comment_id: Id };

export const Annotation = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('rect'),
    geometry: RectGeometry,
    ...annotationStyle,
    ...annotationIds,
  }),
  z.object({
    kind: z.literal('ellipse'),
    geometry: EllipseGeometry,
    ...annotationStyle,
    ...annotationIds,
  }),
  z.object({
    kind: z.literal('path'),
    geometry: PathGeometry,
    ...annotationStyle,
    ...annotationIds,
  }),
  z.object({
    kind: z.literal('arrow'),
    geometry: ArrowGeometry,
    ...annotationStyle,
    ...annotationIds,
  }),
  z.object({
    kind: z.literal('text'),
    geometry: TextGeometry,
    ...annotationStyle,
    ...annotationIds,
  }),
]);
export type Annotation = z.infer<typeof Annotation>;
